//
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { version as currentVersion } from '../../../package.json';
import type {
  IApplicationMetaDataRecommendedAppVersionItem,
  IGlobalStore,
} from './globalStore.model';
import { LOCALES } from '@/shared/models/constants.models';

// TODO: remove getters that are direct access to state withour computations
export const getLocale = (state: IGlobalStore) => state.locale;
export const getDateFormat = (state: IGlobalStore) => {
  // ! https://github.com/date-fns/date-fns/blob/main/docs/unicodeTokens.md
  return state.locale.includes('fr') ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
};
export const getAgent = (state: IGlobalStore) => (agentId: string) =>
  state.agents.find((a) => a.agentId === agentId);
export const containsFeature = (state: IGlobalStore) => (feature: string) =>
  !!state.features.find((f) => f.feature === feature && f.flag);
export const langName = (state: IGlobalStore) => {
  switch (state.locale) {
    case LOCALES.en_US:
      return 'English';
    case LOCALES.en_CA:
      return 'English CA';
    case LOCALES.es_US:
      return 'Spanish';
    case LOCALES.fr_CA:
      return 'French';
    default:
      return 'English';
  }
};

// Return the list of documents for given policy number
export const getDocumentsOfPolicy = (state: IGlobalStore) => (policyNumber: string) => {
  const doc1 = state.documents?.policyDocuments?.filter((a) => a.contract === policyNumber) ?? [];
  const doc2 = state.documents?.notices?.filter((a) => a.contract === policyNumber) ?? [];
  return doc1.concat(doc2);
};

// Return the Full Document for given policy number
export const getFullPolicyDocument = (state: IGlobalStore) => (policyNumber: string) => {
  const doc = state.documents?.policyDocuments?.filter(
    (a) => a.contract === policyNumber && a.documentType === 'A'
  );
  return doc ? doc[0] : undefined;
};

export const policyDocuments = (state: IGlobalStore) => state.documents?.policyDocuments;
export const noticesAndConfirmations = (state: IGlobalStore) => state.documents?.notices;
export const investmentStatements = (state: IGlobalStore) => state.documents?.investmentDocuments;
export const getNavigationPathOnUserConfirmation = (state: IGlobalStore) =>
  state.navigationPathOnUserConfirmation;
export const isMaintenanceMode = (state: IGlobalStore) => {
  return state.meta.isMaintenanceMode;
};
export const hasTermsOfUseTask = (state: IGlobalStore) =>
  state.tasks.find((a) => a.name === 'TERMS_OF_USE');
export const hasPolicyAckTask = (state: IGlobalStore) =>
  state.tasks.find((a) => a.name === 'POLICY_ACK' && a.shownFlag === false);

const _compareVersion = (ver1: string, ver2: string) => {
  if (typeof ver1 !== 'string') return false;
  if (typeof ver2 !== 'string') return false;
  if (ver1 === ver2) {
    return false;
  }
  const ver1Arr: string[] | number[] = ver1.split('.');
  const ver2Arr: string[] | number[] = ver2.split('.');
  const k = Math.min(ver1Arr.length, ver2Arr.length);
  for (let i = 0; i < k; ++i) {
    const ver1ArrNum = parseInt(ver1Arr[i], 10);
    const ver2ArrNum = parseInt(ver2Arr[i], 10);
    if (ver1ArrNum > ver2ArrNum) return true;
    if (ver1ArrNum < ver2ArrNum) return false;
  }
  if (ver1Arr.length === ver2Arr.length) {
    return true;
  } else if (ver1Arr.length < ver2Arr.length) {
    return false;
  }
  return true;
};

const _reducer = (
  prev: IApplicationMetaDataRecommendedAppVersionItem,
  current: IApplicationMetaDataRecommendedAppVersionItem
) => (_compareVersion(prev.version, current.version) ? prev : current);

export const isCriticalUpdateAvailable = (state: IGlobalStore) => {
  const cv = currentVersion;
  // getting the max version number for critical version
  let v;
  if (
    CapacitorFacade.isIos() &&
    state.meta.recommendedAppVersion &&
    state.meta.recommendedAppVersion.ios.length > 0
  ) {
    const t = state.meta.recommendedAppVersion.ios.filter((item) => item.critical);
    v = t.reduce(_reducer, t[0]);
  } else if (
    CapacitorFacade.isAndroid() &&
    state.meta.recommendedAppVersion &&
    state.meta.recommendedAppVersion.android.length > 0
  ) {
    const t = state.meta.recommendedAppVersion.android.filter((item) => item.critical);
    v = t.reduce(_reducer, t[0]);
  } else {
    return false;
  }
  return _compareVersion(v.version, cv);
};

export const isOptionalUpdateAvailable = (state: IGlobalStore) => {
  const cv = currentVersion;
  // getting the max version number for non critical version
  let v;
  if (
    CapacitorFacade.isIos() &&
    state.meta.recommendedAppVersion &&
    state.meta.recommendedAppVersion.ios.length > 0
  ) {
    const t: IApplicationMetaDataRecommendedAppVersionItem[] | undefined =
      state.meta.recommendedAppVersion.ios.filter((item) => item.critical === false);
    v = t.reduce(_reducer, t[0]);
  } else if (
    CapacitorFacade.isAndroid() &&
    state.meta.recommendedAppVersion &&
    state.meta.recommendedAppVersion.android.length > 0
  ) {
    const t: IApplicationMetaDataRecommendedAppVersionItem[] | undefined =
      state.meta.recommendedAppVersion.android.filter((item) => item.critical === false);
    v = t.reduce(_reducer, t[0]);
  } else {
    return false;
  }
  return _compareVersion(v.version || '', cv);
};

export const getAndroidPlayStoreLink = (state: IGlobalStore) => {
  // const cv = currentVersion;
  // getting the max version number for non critical version
  const t = state.meta.recommendedAppVersion?.android;
  const v = t?.reduce(_reducer, t[0]);
  return v?.link ?? '';
};

export const getiOSStoreLink = (state: IGlobalStore) => {
  const t = state.meta.recommendedAppVersion?.ios;
  const v = t?.reduce(_reducer, t[0]);
  return v?.link ?? '';
};

// Return the list of documents for given policy number
export const getPolicyDocuments = (state: IGlobalStore) => (policyNumber: string) => {
  const doc1 = state.documents?.policyDocuments?.filter((a) => a.contract === policyNumber) ?? [];
  const doc2 = state.documents?.notices?.filter((a) => a.contract === policyNumber) ?? [];
  return doc1.concat(doc2);
};
